import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useTranslation } from 'react-i18next';

import { Notification } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';

import { useCreateDepartment, useDepartments } from '../model/useDepartments';

function isDefaultDepartment(department: { is_default?: boolean; isDefault?: boolean }) {
  return department.is_default ?? department.isDefault ?? false;
}

function isActiveDepartment(department: { is_active?: boolean; isActive?: boolean }) {
  return department.is_active ?? department.isActive ?? true;
}

export const DepartmentsPanel = ({ companyId }: { companyId: string }) => {
  const { t } = useTranslation();
  const { data, isPending, isError, error } = useDepartments(companyId);
  const createMutation = useCreateDepartment(companyId);
  const [name, setName] = useState('');

  if (isPending) return <Loader containerSize="medium" />;

  if (isError) {
    return (
      <>
        <NotFoundData title={t('company:team.departmentsLoadError')} />
        <Notification open={isError} title={error?.message} />
      </>
    );
  }

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await createMutation.mutateAsync({ name: trimmed });
    setName('');
  };

  return (
    <Box>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('company:team.departmentsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('company:team.departmentsCaption')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {(data ?? []).map((department) => (
            <Chip
              key={department.id}
              label={department.name}
              color={isDefaultDepartment(department) ? 'primary' : 'default'}
              variant={isActiveDepartment(department) ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          <TextField
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            label={t('company:team.newDepartment')}
            sx={{ minWidth: { xs: '100%', sm: 280 } }}
          />
          <Button
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
            disabled={createMutation.isPending || name.trim().length === 0}
            onClick={handleCreate}
          >
            {t('company:team.createDepartment')}
          </Button>
        </Stack>
      </Stack>

      <Notification
        open={createMutation.isError}
        title={createMutation.error?.message}
      />
    </Box>
  );
};
